import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {useI18n} from '@udemy/i18n';
import {useViewport, withinOrWiderThan} from '@udemy/react-layout-components';
import {ShowMore} from '@udemy/react-reveal-components';
import {
    Carousel,
    CarouselMutationEvent,
    CarouselNextButton,
    CarouselPreviousButton,
    CarouselProps,
    dispatchCarouselNextPageEvent,
    dispatchCarouselPreviousPageEvent,
} from '@udemy/react-structure-components';
import {SlideIndicatorDots} from '@udemy/shared-one-udemy-components';

import styles from './enriched-skills.module.less';

interface EnrichedSkillsProps extends Pick<CarouselProps, 'onPreviousClick' | 'onNextClick'> {
    /**
     * The heading element to use for the heading.
     *
     * @defaultValue 'h3'
     */
    headingElement?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    /**
     * The title of the career life skills section.
     *
     * @defaultValue 'Learn essential career and life <span style="color: var(--color-purple-300);">skills</span>'
     */
    title?: string;
    /**
     * The subtitle of the career life skills section.
     *
     * @defaultValue 'Our platform delivers, AI-powered skills acceleration to help you build in-demand skills fast and advance your career in a changing job market.'
     */
    subtitle?: string;
    /**
     * Event handler for the previous button on desktop carousel
     */
    onPreviousClick?: () => void;
    /**
     * Event handler for the next button on desktop carousel
     */
    onNextClick?: () => void;
    /**
     * The children of the career life skills section.
     */
    children: React.ReactNode;
}

export const EnrichedSkills = ({
    headingElement = 'h3',
    title,
    subtitle,
    onPreviousClick,
    onNextClick,
    children,
}: EnrichedSkillsProps) => {
    const {gettext} = useI18n();
    const [pagerEventTarget, setPagerEventTarget] = useState<EventTarget>();
    const [disableNextButton, setDisableNextButton] = useState(false);
    const [disablePrevButton, setDisablePrevButton] = useState(false);
    const [visiblePageIndex, setVisiblePageIndex] = useState(0);
    const {range} = useViewport();

    const titleString =
        title ??
        gettext(
            'Learn essential career and life <span style="color: var(--color-purple-300);">skills</span>',
        );
    const subtitleString =
        subtitle ??
        gettext(
            'Our platform delivers, AI-powered skills acceleration to help you build in-demand skills fast and advance your career in a changing job market.',
        );

    const HeadingElement = headingElement;
    const slideDenominator = range && withinOrWiderThan(range, 'xl') ? 3 : 2;
    const isDesktop = range && withinOrWiderThan(range, 'sm');
    const slides = Math.ceil(React.Children.toArray(children).length / slideDenominator);

    const handleMutation = (event: CarouselMutationEvent) => {
        setVisiblePageIndex(event.visiblePageIndex as number);

        if (event.isLastPage && event.visiblePageIndex !== null) {
            setDisableNextButton(true);
        } else if (disableNextButton) {
            setDisableNextButton(false);
        }
        if (event.visiblePageIndex === 0) {
            setDisablePrevButton(true);
        } else if (disablePrevButton) {
            setDisablePrevButton(false);
        }
    };

    const customNextClick = () => {
        dispatchCarouselNextPageEvent(pagerEventTarget as EventTarget);
    };
    const customPrevClick = () => {
        dispatchCarouselPreviousPageEvent(pagerEventTarget as EventTarget);
    };

    useEffect(() => {
        // `EventTarget` is not supported nodeJS 14.*, so instantiate it client-side in browser.
        setPagerEventTarget(new EventTarget());
    }, []);

    const renderDektopCarousel = () => (
        <>
            <Carousel
                data-testid="carousel"
                className={styles['carousel-9-column']}
                showPager={false}
                onMutation={handleMutation}
                onNextClick={onNextClick}
                onPreviousClick={onPreviousClick}
                pagerEventTarget={pagerEventTarget}
            >
                {children}
            </Carousel>
            <div className={styles['pager-buttons-wrapper']}>
                <CarouselPreviousButton
                    data-testid="prev-button"
                    pagerButtonSize="medium"
                    className={classNames(styles['slide-indicator-pager'], {
                        [styles.disabled]: disablePrevButton,
                    })}
                    disabled={disablePrevButton}
                    onClick={customPrevClick}
                    udStyle={'white-solid'}
                />
                <SlideIndicatorDots pageCount={slides} visiblePageIndex={visiblePageIndex} />
                <CarouselNextButton
                    pagerButtonSize="medium"
                    className={classNames(styles['slide-indicator-pager'], {
                        [styles.disabled]: disableNextButton,
                    })}
                    disabled={disableNextButton}
                    onClick={customNextClick}
                    udStyle={'white-solid'}
                />
            </div>
        </>
    );

    const renderMobileCards = () => (
        <ShowMore data-testid="show-more" collapsedHeight={824}>
            <div className={styles['mobile-card-grid']}>{children}</div>
        </ShowMore>
    );

    return (
        <section
            className={classNames('ud-full-viewport-container', styles['enriched-skills-wrapper'])}
        >
            <div className="ud-container">
                <div className={styles['layouts-for-cards']}>
                    <div className={classNames(styles['layout-12-column-container'])}>
                        <div className={styles['placeholder-3-column']}>
                            <HeadingElement
                                className={classNames(styles.heading, 'ud-heading-xxl')}
                                dangerouslySetInnerHTML={{__html: titleString}}
                            ></HeadingElement>
                            <p className={classNames(styles.description, 'ud-text-md')}>
                                {subtitleString}
                            </p>
                        </div>
                        <div className={styles['carousel-9-column-container']}>
                            {isDesktop && renderDektopCarousel()}
                            {!isDesktop && renderMobileCards()}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
